import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { CalendarDays } from "lucide-react";

const PANEL_H = 400;
const PANEL_W = 300;

const iso = (d) => {
  if (!d) return "";
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

const parse = (v) => {
  if (!v) return undefined;
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d) ? undefined : d;
};

const pretty = (v) =>
  parse(v)?.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

export const DateField = ({ label: title, value, onChange, testId, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const selected = parse(value);

  // fixed positioning with viewport collision flip, so the calendar is never clipped
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const place = () => {
      const r = triggerRef.current.getBoundingClientRect();
      const below = window.innerHeight - r.bottom;
      const flip = below < PANEL_H && r.top > below;
      const left = Math.min(Math.max(8, r.left), Math.max(8, window.innerWidth - PANEL_W - 8));
      const space = flip ? r.top : below;
      setPos({
        left,
        top: flip ? undefined : Math.min(r.bottom + 6, window.innerHeight - 16),
        bottom: flip ? Math.max(8, window.innerHeight - r.top + 6) : undefined,
        maxHeight: space < PANEL_H ? Math.max(240, space - 16) : undefined,
      });
      if (panelRef.current) panelRef.current.scrollTop = 0;
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commit = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div>
      {title && <span className="kicker block mb-2">{title}</span>}
      <button
        ref={triggerRef}
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className="field flex items-center justify-between gap-3 text-left"
        style={{ color: value ? "var(--ink)" : "var(--ink-faint)" }}
      >
        <span className="truncate">{value ? pretty(value) : placeholder || "Select a date"}</span>
        <CalendarDays size={15} style={{ color: "var(--brass)" }} className="shrink-0" />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[130]" onClick={() => setOpen(false)} />
          <div
            ref={panelRef}
            className="card-t fixed z-[140] !p-4 fade-in overflow-y-auto"
            data-testid={testId ? `${testId}-calendar` : undefined}
            style={{ ...pos, width: PANEL_W }}
          >
            <DayPicker
              mode="single"
              selected={selected}
              defaultMonth={selected}
              showOutsideDays
              onSelect={(d) => commit(iso(d))}
              classNames={{
                months: "flex",
                month: "space-y-3 w-full",
                caption: "flex items-center justify-between px-1",
                caption_label: "serif text-lg",
                nav: "flex gap-1",
                nav_button:
                  "h-7 w-7 inline-flex items-center justify-center rounded-md border border-[var(--hairline)] hover:border-[var(--brass-bright)] transition-colors",
                table: "w-full border-collapse",
                head_row: "flex",
                head_cell:
                  "w-9 text-[9px] uppercase tracking-[0.16em] text-[var(--brass)] font-medium",
                row: "flex w-full mt-1",
                cell: "w-9 h-9 text-center",
                day: "w-9 h-9 rounded-md text-[13px] hover:bg-[rgba(37,99,235,.10)] transition-colors",
                day_selected:
                  "!bg-[var(--ink-panel)] !text-[#FFFFFF] hover:!bg-[var(--ink-panel)]",
                day_today: "border border-[var(--brass-bright)]",
                day_outside: "opacity-30",
              }}
            />
            <div className="hair my-3" />
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="btn-quiet !min-h-0 !py-1.5"
                data-testid={testId ? `${testId}-today` : undefined}
                onClick={() => commit(iso(new Date()))}
              >
                Today
              </button>
              <button
                type="button"
                className="btn-quiet !min-h-0 !py-1.5"
                data-testid={testId ? `${testId}-clear` : undefined}
                onClick={() => commit("")}
              >
                Clear
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default DateField;
